import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Trash2, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { projectsApi } from '../lib/api';
import { StageEditor } from './StageEditor';

const INPUT_CLASS = "bg-[#1A1D21] border-[#33383F] text-[#F0EDE8] placeholder:text-[#555D68] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl";

export function EditProjectDialog({ open, onOpenChange, project, onUpdate, onDelete, onArchived }) {
  const [form, setForm] = useState(null);
  const [stages, setStages] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (project) {
      setForm({
        ...project,
        target_completion_date: project.target_completion_date || ''
      });
      setStages(project.stages || []);
    }
  }, [project]);

  if (!form) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, stages };
    if (!payload.target_completion_date) payload.target_completion_date = null;
    const success = await onUpdate(form.id, payload);
    if (success) onOpenChange(false);
  };

  const handleConfirmDelete = async () => {
    const success = await onDelete(form.id, { skipConfirm: true });
    setConfirmDelete(false);
    if (success !== false) onOpenChange(false);
  };

  const handleConfirmArchive = async () => {
    setArchiving(true);
    try {
      await projectsApi.archive(form.id);
      toast.success('Project archived');
      setConfirmArchive(false);
      if (onArchived) onArchived();
      onOpenChange(false);
    } catch {
      toast.error('Failed to archive project');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#23272E] border-[#33383F] rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#F0EDE8] font-bold tracking-tight">Edit Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-[#F0EDE8] font-medium">Project Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={INPUT_CLASS}
              data-testid="edit-project-title-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-[#F0EDE8] font-medium">Client Name</Label>
            <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              className={INPUT_CLASS}
              data-testid="edit-project-client-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-[#F0EDE8] font-medium">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={`${INPUT_CLASS} min-h-[80px]`}
              data-testid="edit-project-description-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-[#F0EDE8] font-medium">Target Completion Date</Label>
            <Input type="date" value={form.target_completion_date || ''}
              onChange={(e) => setForm({ ...form, target_completion_date: e.target.value })}
              className={INPUT_CLASS}
              data-testid="edit-project-target-date-input" />
          </div>
          <StageEditor stages={stages} onChange={setStages} />
          <div className="space-y-2">
            <Label className="text-[#F0EDE8] font-medium">Notes</Label>
            <Textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Internal notes..."
              className={`${INPUT_CLASS} min-h-[60px]`}
              data-testid="edit-project-notes-input" />
          </div>
          <div className="flex justify-between items-center gap-2 pt-4">
            <div className="flex gap-2">
              {onDelete && (
                <Button type="button" variant="outline" onClick={() => setConfirmDelete(true)}
                  className="border-[#DC2626]/40 text-[#DC2626] hover:bg-[#DC2626]/10 hover:text-[#DC2626] rounded-xl"
                  data-testid="edit-project-delete">
                  <Trash2 className="w-4 h-4 mr-1.5" />Delete
                </Button>
              )}
              {form.status !== 'quote' && !form.archived && (
                <Button type="button" variant="outline" onClick={() => setConfirmArchive(true)}
                  className="border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl"
                  data-testid="edit-project-archive">
                  <Archive className="w-4 h-4 mr-1.5" />Archive
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                className="border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl">Cancel</Button>
              <Button type="submit" className="bg-[#4A9D6E] hover:bg-[#3D8A5E] text-white rounded-xl"
                data-testid="edit-project-submit">Save Changes</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>

    <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
      <AlertDialogContent className="bg-[#23272E] border-[#33383F] rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#F0EDE8]">Delete this project?</AlertDialogTitle>
          <AlertDialogDescription className="text-[#9CA3AF]">
            &quot;{form.title}&quot; and all of its stages, photos, files, and notes will be permanently removed. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="border-[#33383F] bg-transparent text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirmDelete}
            className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-xl"
            data-testid="edit-project-delete-confirm">
            Delete Project
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
      <AlertDialogContent className="bg-[#23272E] border-[#33383F] rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#F0EDE8]">Archive this project?</AlertDialogTitle>
          <AlertDialogDescription className="text-[#9CA3AF]">
            &quot;{form.title}&quot; will be marked as complete and moved to the Archived list. You can restore it from there any time.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="border-[#33383F] bg-transparent text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirmArchive} disabled={archiving}
            className="bg-[#4A9D6E] hover:bg-[#3D8A5E] text-white rounded-xl"
            data-testid="edit-project-archive-confirm">
            {archiving ? 'Archiving...' : 'Archive Project'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

